/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import { Observable, catchError, from, map, of } from "rxjs";
import type { TablesKeys, Views, ViewsKeys } from "../supabase";
import { ItemsGenericServiceBase } from "./items-generic-service-base";
import type { ItemsService } from "./items-service-interface";
type NonNullProperties<T> = {
	[P in keyof T]: Exclude<T[P], null>;
};
export abstract class ItemsViewServiceBase<Entity extends Views<ViewsKeys>>
	extends ItemsGenericServiceBase<Entity>
	implements ItemsService<Entity> {
	public realtimeReferenceTables: TablesKeys[] = [];

	public readonly realtimeUpdate = new Observable<
		{
			type: "INSERT" | "UPDATE" | "DELETE" | "GENERIC";
			table: string;
			data: Record<string, unknown>;
		}
	>((observer) => {
		this.onRealtimeFullyReady().subscribe(() => {
			if (!this.realtimeReferenceTables) {
				throw new Error(`realtimeTable is not set for view ${this.TABLE}`);
			}

			const channel = this.supabaseClient.channel(this.TABLE);

			for (const table of this.realtimeReferenceTables) {
				channel.on(
					"postgres_changes",
					{
						event: "*",
						schema: "public",
						table: table,
					},
					(event) => {
						switch (event.eventType) {
							case "INSERT":
								observer.next({
									type: "INSERT",
									table: table,
									data: event.new,
								});
								break;
							case "UPDATE":
								observer.next({
									type: "UPDATE",
									table: table,
									data: event.new,
								});
								break;
							case "DELETE":
								observer.next({
									type: "DELETE",
									table: table,
									data: event.old,
								});
								break;
							default:
								observer.next({
									type: "GENERIC",
									table: table,
									data: {},
								});
						}
					},
				);
			}

			channel.subscribe()
		});
	});

	public postDefault(entity?: Entity): Observable<void> {
		return this.postOne(entity ?? this.defaultItem());
	}

	public postOne(entity: Partial<Entity>): Observable<void> {
		if (this.hasTenant) {
			(<any>entity).tenant_id = this.tenantId();
		}
		return from(
			this.supabaseClient.rpc(`insert_into_${this.TABLE}` as any, {
				_record: entity as NonNullProperties<Entity>,
			}),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);
	}

	public postMany(entities: Partial<Entity>[]): Observable<void> {
		if (this.hasTenant) {
			for (const entity of entities) {
				(<any>entity).tenant_id = this.tenantId();
			}
		}
		return from(
			this.supabaseClient.from(this.TABLE).upsert(entities as any),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);
	}

	public upsert(entities: Entity[], onConflict?: string): Observable<void> {
		return this.putMany(entities, onConflict);
	}

	public putMany(entities: Entity[], onConflict?: string): Observable<void> {
		if (this.hasTenant) {
			for (const entity of entities) {
				if ("tenant_id" in entity) {
					const tenantId = this.tenantId();
					if (!tenantId) {
						//throw new Error("Tenant ID is not set");
						return of();
					}

					entity.tenant_id = tenantId;
				}
			}
		}

		return from(
			onConflict
				? this.supabaseClient.from(this.TABLE).upsert(entities, {
					onConflict: onConflict,
				})
				: this.supabaseClient.from(this.TABLE).upsert(entities),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);
	}

	public putOne(entity: Entity): Observable<void> {
		if (this.hasTenant) {
			(<any>entity).tenant_id = this.tenantId();
		}
		return from(
			this.supabaseClient.rpc(`update_${this.TABLE}` as any, {
				_record: entity as NonNullProperties<Entity>,
			}),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);
	}

	public deleteOne(entity: Entity): Observable<void> {
		return from(
			this.supabaseClient.rpc(`delete_from_${this.TABLE}` as any, {
				_record: entity,
			}),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);
	}

	public deleteMany(entities: Entity[]): Observable<void> {
		if (!entities || entities.length === 0) {
			return of();
		}
		console.log(this.TABLE);
		return from(
			this.supabaseClient.rpc("delete_many_view", {
				records: entities.map((id) => ({ id })),
				view_name: this.TABLE,
			}),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);

		/* return forkJoin(
			entities.map((entity) =>
				this.deleteOne(entity).pipe(catchError((error) => of(error))),
			),
		).pipe(map(() => void 0)); */
	}
}
